import { useState } from 'react'
import { X, MailCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { swimApi } from '@swim/api-client'
import { useSession } from '@/hooks/use-session'
import { useNotify } from '@/hooks/use-notify'
import { useLocation } from 'react-router-dom'

// Routes where the banner should never appear — auth modal pages dim the chrome
const SUPPRESS_ROUTES = new Set([
  '/sign-in', '/sign-up', '/forgot-password', '/reset-password', '/verify-email',
])

/**
 * AppBanner — full-width contextual banner slot.
 *
 * Currently surfaces one banner: "verify your email" for email/password users
 * with emailVerified = false. Google OAuth users are auto-verified and never see it.
 *
 * Dismissal is session-scoped (local state). No DB persistence — if the user
 * refreshes the banner reappears until they verify. This is intentional: the
 * banner is a nudge, not an announcement.
 *
 * Extend this component for future banners (maintenance windows, feature flags, etc.)
 * by adding more banner conditions before the verify check.
 */
export function AppBanner() {
  const { t } = useTranslation('common')
  const { user, isAuthenticated } = useSession()
  const notify = useNotify()
  const location = useLocation()
  const [dismissed, setDismissed] = useState(false)
  const [resending, setResending] = useState(false)

  // Suppress on auth modal routes
  const pathKey = location.pathname.split('/').slice(3).join('/') // strip /:locale/:lang prefix
  const fullPath = '/' + pathKey
  if (SUPPRESS_ROUTES.has(fullPath)) return null

  // Only show for authenticated users who haven't verified their email
  // Google OAuth users always have emailVerified = true — they never see this
  if (!isAuthenticated || !user || user.emailVerified || dismissed) return null

  async function handleResend() {
    setResending(true)
    await swimApi.post<unknown>('/auth/customer/send-verification-email', { email: user!.email })
    setResending(false)
    notify.success(t('email.verificationSent'))
  }

  return (
    <div
      role="alert"
      aria-live="polite"
      className="flex-shrink-0 flex items-center justify-between gap-3 mx-3 mb-2 px-4 py-2.5 rounded-xl bg-amber-50 dark:bg-amber-950/40 border border-amber-200 dark:border-amber-800/60 text-amber-900 dark:text-amber-200"
    >
      <div className="flex items-center gap-2 min-w-0">
        <MailCheck className="h-4 w-4 flex-shrink-0 text-amber-600 dark:text-amber-400" />
        <p className="text-sm font-sans truncate">
          {t('email.verifyMessage')}
        </p>
        <button
          type="button"
          onClick={handleResend}
          disabled={resending}
          className="text-sm font-medium font-sans underline underline-offset-2 hover:no-underline flex-shrink-0 disabled:opacity-50 transition-opacity"
        >
          {resending ? t('email.sending') : t('email.resend')}
        </button>
      </div>

      <button
        type="button"
        onClick={() => setDismissed(true)}
        aria-label={t('actions.dismiss')}
        className="flex-shrink-0 p-1 rounded hover:bg-amber-100 dark:hover:bg-amber-900/40 transition-colors"
      >
        <X className="h-3.5 w-3.5" />
      </button>
    </div>
  )
}
